import { useTextEditorState, useUpdatePin } from '@lessonup/pin-renderer';
import {
  DropZonePinComponent,
  FontFamily,
  initialPinComponentPosition,
  isDragAndDropPin,
  newHotspotPinComponent,
  newShapePinComponent,
  newSize,
  newTextPinComponent,
  PinComponent,
  randomId,
  Vector,
  TypographyType,
  TypographySettings,
  fontSizes,
} from '@lessonup/pins-shared';
import {
  IconComponentHotspot,
  IconComponentImage,
  IconComponentShape,
  IconComponentText,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import React, { useRef } from 'react';
import { ContextSidebarCollapsibleGroup } from '../../../../../../../../components/ContextSidebar/ContextSidebarGroup';
import { useFeatureFlags } from '../../../../../../../../utils/featureFlags/useFeatureFlags';
import { Toolbelt, ToolbeltItem } from '../../../../../../components/EditorTopBar/components/Toolbelt/Toolbelt';
import { useEditorContext } from '../../../../../../context/EditorContext/EditorContext';
import {
  useActivePin,
  useActivePinTheme,
  useEditMode,
  useEffectiveActivePinComponentSettings,
} from '../../../../../../context/EditorContext/hooks/useEditorState';
import { useEditorTranslation } from '../../../../../../hooks/useEditorTranslation';
import { calculateInitialPosition } from '../../../../../../utils/pinComponent/position';
import {
  createFontFamilyOptions,
  createFontWeightStyleOptions,
  createTypographyOptions,
  FontWeightStyle,
  getFontWeightStyleValue,
  onFontWeightStyleChange,
} from '../../../../../../utils/pinComponentSettings/textPinComponent';
import { teacherTextEditorStore } from '../../../../../../utils/yjs/EditorYjs/teacherTextEditorStore';
import { ShapePicker } from '../../../../../EditorModals/ShapePicker/ShapePicker';
import { NoFocusPicker, NoFocusPickerProps } from '../../../CustomSelect/NoFocusPicker';
import { SteppedNumberInput } from '../../../CustomSelect/SteppedNumberInput';
import { ColorPickerSetting } from '../../../SharedSettings/ColorPickerSetting';
import { BackgroundColorSetting } from '../../sharedComponentSettings/BackgroundColorSetting';
import { BorderColorSetting } from '../../sharedComponentSettings/BorderColorSetting';
import { BorderRadiusSetting } from '../../sharedComponentSettings/BorderRadiusSetting';
import { BorderSizeSetting } from '../../sharedComponentSettings/BorderSizeSetting';
import { LayoutPositionSettingGroup } from '../../sharedComponentSettings/LayoutPositionSetting';
import { useHandleCreateImageAttach } from '../../sharedComponentSettings/hooks/useHandleCreateImageAttach';
import { PinComponentSettingsProps } from '../../types';
import { DropZoneBackgroundImageSettings } from './DropZoneBackgroundImageSettings';
import { DropzonePinComponentPicker } from './DropzonePinComponentPicker';

export const DropZonePinComponentSettings: React.FC<PinComponentSettingsProps<DropZonePinComponent>> = ({
  pinComponent,
}) => {
  const dispatch = useUpdatePin();
  const pin = useActivePin();
  const { setModeSelecting } = useEditorContext();
  const { isFeatureEnabled } = useFeatureFlags();
  const { textHandler: textEditorHandler } = useTextEditorState(teacherTextEditorStore);
  const { typography: typographySettings } = useActivePinTheme();
  const editMode = useEditMode();
  const isTextEditMode = editMode.type === 'textEdit';
  const pinComponentSettings = useEffectiveActivePinComponentSettings('DROP_ZONE', { settings: pinComponent.settings });

  const shouldUpdateInline = () => textEditorHandler.updateShouldTargetChild(isTextEditMode);

  const { t } = useEditorTranslation();
  const addImageComponentRef = useRef<HTMLLIElement>(null);

  const handleCreateImageAttach = useHandleCreateImageAttach(pinComponent, addImageComponentRef, 'DROPZONE');

  const fontSizeToInt = (fontSize: string) => parseInt(fontSize.replace('px', ''));
  const intToFontSize = (fontSize: number) => `${fontSize}px`;

  const createFontSizeOptions = () =>
    fontSizes.map((fontSize) => ({ label: fontSize, value: fontSizeToInt(fontSize) }));

  if (!pin || !isDragAndDropPin(pin)) return null;

  const newLinkedDragItem = (newComponent: (id: string, position: Vector) => PinComponent) => {
    const id = randomId();
    const position = calculateInitialPosition(initialPinComponentPosition, pin?.pinComponents);
    const comp = newComponent(id, position);
    const linkedPinComponents = { ...pin.settings.linkedPinComponents, [id]: [pinComponent.id] };
    dispatch([
      {
        type: 'addPinComponents',
        pinComponents: [comp],
        onComplete: () => {
          setModeSelecting([comp.id]);
        },
      },
      {
        type: 'updatePinSettings',
        settings: { linkedPinComponents: linkedPinComponents },
      },
    ]);
  };

  const fontFamilyPicker: NoFocusPickerProps<FontFamily> = {
    value: pinComponentSettings.fontFamily as FontFamily,
    options: createFontFamilyOptions(),
    styleOption: (option) => ({ fontFamily: `"${option?.value}"` }),
    onChange: (value) => {
      if (shouldUpdateInline()) {
        textEditorHandler.fontFamily.set(value);
      } else {
        dispatch({
          type: 'updatePinComponentSettings',
          pinComponentId: pinComponent.id,
          settings: {
            fontFamily: value,
          },
        });
        textEditorHandler.fontFamily.unsetAll();
      }
    },
  };

  const weightValue = getFontWeightStyleValue(textEditorHandler);
  const fontWeightStylePicker: NoFocusPickerProps<FontWeightStyle> = {
    value: weightValue,
    valueLabel: t(`contextSidebar.text.fontWeightStyle.${weightValue}`),
    options: createFontWeightStyleOptions(t),
    styleOption: (option) => ({
      fontFamily: pinComponentSettings.fontFamily,
      fontWeight: option?.value === 'bold' || option?.value === 'bold-italic' ? 'bold' : 'normal',
      fontStyle: option?.value === 'regular-italic' || option?.value === 'bold-italic' ? 'italic' : 'normal',
    }),
    onChange: (value) => {
      onFontWeightStyleChange(textEditorHandler, value);
    },
  };

  const typographyKey = pinComponentSettings.typographyKey;
  const typographyTypePicker: NoFocusPickerProps<TypographyType> = {
    value: typographyKey,
    valueLabel: typographyKey ? t(`typographyType.${typographyKey}`) : undefined,
    options: createTypographyOptions(),
    styleOption: (option) => styleForTypographyValue(option?.value, option?.value && typographySettings[option?.value]),
    onChange: (value) => {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          typographyKey: value,
          fontFamily: undefined,
          fontSize: undefined,
        },
      });
      textEditorHandler.fontFamily.unsetAll();
      textEditorHandler.fontSize.unsetAll();
      if (typographySettings[value]?.fontWeight === '700') {
        textEditorHandler.bold.setAll?.();
      } else {
        textEditorHandler.bold.unsetAll?.();
      }
    },
  };

  return (
    <>
      <LayoutPositionSettingGroup />

      <StyledToolbeltContainer>
        <TextElement textStyle="label" size="medium" weight="bold">
          {t('contextSidebar.settings.addDragItem')}
        </TextElement>
        <Toolbelt>
          <ToolbeltItem
            icon={<IconComponentText />}
            text={t('toolbelt.components.text')}
            onClick={() =>
              newLinkedDragItem((id, position) =>
                newTextPinComponent({
                  id,
                  position,
                  size: newSize(175, 50),
                })
              )
            }
          />
          <ToolbeltItem
            ref={addImageComponentRef}
            icon={<IconComponentImage />}
            text={t('toolbelt.components.image')}
            onClick={handleCreateImageAttach}
          />
          <ToolbeltItem
            icon={<IconComponentHotspot />}
            text={t('toolbelt.components.hotspot')}
            onClick={() => newLinkedDragItem((id, position) => newHotspotPinComponent({ id, position }))}
          />
          <ToolbeltItem
            icon={<IconComponentShape />}
            text={t('toolbelt.components.shape')}
            modalBody={(close) => (
              <ShapePicker
                dispatch={dispatch}
                setModeSelecting={setModeSelecting}
                close={close}
                shapePickerHandler={(shapeType) =>
                  newLinkedDragItem((id, position) => newShapePinComponent({ id, position, shapeType }))
                }
              />
            )}
          />
        </Toolbelt>
      </StyledToolbeltContainer>

      <ContextSidebarCollapsibleGroup
        persistId="dropzoneLinkedComponentSetting"
        title={t('contextSidebar.settings.linkToDragItem')}
        isCollapsed={false}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <DropzonePinComponentPicker pinComponent={pinComponent} />
      </ContextSidebarCollapsibleGroup>

      <ContextSidebarCollapsibleGroup
        persistId="dropZoneContent"
        title={t('contextSidebar.settings.content.body')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="y" spacing={spacing.size12}>
          {isFeatureEnabled('editorPinTheme') && <NoFocusPicker {...typographyTypePicker} />}
          <NoFocusPicker {...fontFamilyPicker} />
          <SpaceBetween direction="x" spacing={spacing.size12}>
            <NoFocusPicker {...fontWeightStylePicker} />
            <SteppedNumberInput
              label="Font Size"
              value={pinComponentSettings.fontSize ? fontSizeToInt(pinComponentSettings.fontSize) : undefined}
              options={createFontSizeOptions()}
              valueValidation={(value) => value === '' || /^\d+$/.test(value)}
              onTextChange={(value) => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: {
                    fontSize: intToFontSize(value),
                  },
                });
              }}
              onChange={(value) => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: {
                    fontSize: intToFontSize(value),
                  },
                });
              }}
            />
          </SpaceBetween>
          <ColorPickerSetting
            selectedColor={pinComponentSettings.fontColor}
            text={t('contextSidebar.settings.textColor')}
            ariaLabel={t('contextSidebar.settings.textColor')}
            hideTransparentOption
            onChange={(color) => {
              if (shouldUpdateInline()) {
                textEditorHandler.color.set(color);
              } else {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: {
                    fontColor: color,
                  },
                  debounce: 1000,
                });
                textEditorHandler.color.unsetAll();
              }
            }}
          />
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>

      <ContextSidebarCollapsibleGroup
        persistId="dropZoneStyling"
        title={t('contextSidebar.categoryArea.backgroundSettings')} // TODO: change the key to contextSidebar.settings.backgroundSettings
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="y" spacing={spacing.size8}>
          <BackgroundColorSetting pinComponent={pinComponent} text={t('contextSidebar.settings.backgroundColor')} />
          <DropZoneBackgroundImageSettings pinComponent={pinComponent} />
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>

      <ContextSidebarCollapsibleGroup
        persistId="dropZoneBorderSettings"
        title={t('contextSidebar.settings.border')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="y" spacing={spacing.size8}>
          <BorderRadiusSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-radius-setting`} />
          <BorderSizeSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-size-setting`} />
          <BorderColorSetting
            pinComponent={pinComponent}
            key={`${pinComponent.id}-border-color-setting`}
            text={t('contextSidebar.settings.borderColor')}
          />
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>
    </>
  );
};

const StyledToolbeltContainer = styled.div`
  > span {
    display: block;
    margin: ${spacing.size16};
  }
  margin-top: ${spacing.size8};
  margin-bottom: ${spacing.size16};
`;

export function styleForTypographyValue(
  value: TypographyType | undefined,
  typography?: TypographySettings
): React.CSSProperties | undefined {
  if (!value) {
    return;
  }

  return {
    fontFamily: typography?.fontFamily,
    fontSize: `calc(${typography?.fontSize} / 2.5)`,
    fontWeight: typography?.fontWeight,
  };
}
